import { useCallback, useState } from "react";
import { useBreadcrumbStackOptional } from "../contexts/BreadcrumbStackContext";

// Module-scoped — survives a details-page round trip within the SPA session,
// cleared on a hard reload. Keyed by list identity (e.g. "assets", "schemes").
const cache = new Map();

/**
 * Remembers { page, pageSize } for a list across a details-page round trip.
 *
 * When `entryId` is given, the cached value is only trusted if that id is
 * still on the breadcrumb stack at mount time — i.e. the user actually came
 * back via the details flow (Back pill / crumb / browser back), not a fresh
 * entry into the list (nav menu, deep link, reload) that happens to reuse
 * the same cache key. Without `entryId` the cache is trusted unconditionally
 * (for lists, like Assets, that don't register onto the breadcrumb stack).
 */
export default function usePersistedPagination(key, { defaultPageSize, entryId } = {}) {
  const stack = useBreadcrumbStackOptional();

  const [state, setState] = useState(() => {
    const cached = cache.get(key);
    if (!cached) return { page: 1, pageSize: defaultPageSize };
    const trusted = !entryId || stack?.entries?.some((entry) => entry.id === entryId);
    if (!trusted) {
      cache.delete(key);
      return { page: 1, pageSize: defaultPageSize };
    }
    return cached;
  });

  const setPage = useCallback(
    (page) => {
      setState((prev) => {
        const next = { ...prev, page };
        cache.set(key, next);
        return next;
      });
    },
    [key]
  );

  const setPageSize = useCallback(
    (pageSize) => {
      const next = { page: 1, pageSize };
      cache.set(key, next);
      setState(next);
    },
    [key]
  );

  return { page: state.page, pageSize: state.pageSize, setPage, setPageSize };
}
